import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from './api';
import type { Identity } from './types';

interface SessionCtx {
  loading: boolean;
  identity: Identity | null; // null = not authenticated
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  /** SuperAdmin org switcher (Section 16.3); null = back to the home org. */
  switchOrg: (orgId: string | null) => Promise<void>;
}
const Ctx = createContext<SessionCtx | null>(null);

const SILENT_KEY = 'silent-sso-tried';
const ACTIVE_ORG_KEY = 'fda-active-org';
const readActiveOrg = () => {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY);
  } catch {
    return null;
  }
};

/** Loads /api/v1/me once; on 401 tries silent SSO exactly once per browser session (Section 2.2 step 2). */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [identity, setIdentity] = useState<Identity | null>(null);

  const refresh = useCallback(async () => {
    try {
      const active = readActiveOrg();
      const idn = await api<Identity>(`/api/v1/me${active ? `?org=${encodeURIComponent(active)}` : ''}`);
      // the server ignores an org the user may not open (or the home org) -> drop a stale selection
      if (active && idn.org?.id !== active) localStorage.removeItem(ACTIVE_ORG_KEY);
      setIdentity(idn);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setIdentity(null);
        const onLogin = window.location.pathname === '/login';
        if (!onLogin && !sessionStorage.getItem(SILENT_KEY)) {
          sessionStorage.setItem(SILENT_KEY, '1');
          window.location.assign(
            `/api/auth/login?silent=1&next=${encodeURIComponent(window.location.pathname + window.location.search)}`,
          );
          return;
        }
      } else throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    const r = await api<{ endSessionUrl: string | null }>('/api/auth/logout', { method: 'POST' });
    sessionStorage.removeItem(SILENT_KEY);
    localStorage.removeItem(ACTIVE_ORG_KEY);
    setIdentity(null);
    window.location.assign(r.endSessionUrl ?? '/login');
  }, []);

  const switchOrg = useCallback(
    async (orgId: string | null) => {
      if (orgId) localStorage.setItem(ACTIVE_ORG_KEY, orgId);
      else localStorage.removeItem(ACTIVE_ORG_KEY);
      await refresh();
    },
    [refresh],
  );

  const value = useMemo(
    () => ({ loading, identity, refresh, logout, switchOrg }),
    [loading, identity, refresh, logout, switchOrg],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its provider
export function useSession(): SessionCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}
/** Same rule as the BFF's safeNext: only same-origin paths. */
// eslint-disable-next-line react-refresh/only-export-components
export const safeNext = (v: string | null | undefined) =>
  v && v.startsWith('/') && !v.startsWith('//') && !v.includes('\\') ? v : '/';
export const loginUrl = (next = window.location.pathname) =>
  `/api/auth/login?next=${encodeURIComponent(safeNext(next))}`;
